type RecorderOptions = {
  fps?: number; // e.g. 15
  mimeTypes?: string[]; // tried in order
  bitsPerSecond?: number; // ~target bitrate
};

export function createCanvasRecorder(
  canvas: HTMLCanvasElement,
  {
    fps = 30,
    mimeTypes = [
      "video/mp4",
      "video/webm;codecs=vp9",
      "video/webm;codecs=vp8",
      "video/webm",
    ],
    bitsPerSecond = 1_500_000, // ≈1.5 Mbps (good for 720p-ish line art)
  }: RecorderOptions = {}
) {
  // Pick the first supported mimeType
  const mimeType =
    mimeTypes.find((m) => MediaRecorder.isTypeSupported(m)) ?? "";
  if (!mimeType) {
    throw new Error("No supported WebM mimeType on this browser.");
  }

  const stream = canvas.captureStream(fps);
  const mediaRecorder = new MediaRecorder(stream, { mimeType, bitsPerSecond });

  const chunks: BlobPart[] = [];
  let blob: Blob | null = null;
  let url: string | null = null;

  mediaRecorder.ondataavailable = (e) => e.data && chunks.push(e.data);
  mediaRecorder.onstop = () => {
    blob = new Blob(chunks, { type: mimeType });
    url = URL.createObjectURL(blob);
  };

  return {
    start() {
      chunks.length = 0;
      blob = null;
      url = null;
      mediaRecorder.start();
    },
    stop(): Promise<{ blob: Blob; url: string }> {
      return new Promise((resolve) => {
        const onStop = () => {
          mediaRecorder.removeEventListener("stop", onStop);
          // Non-null because onstop has fired
          resolve({ blob: blob!, url: url! });
        };
        mediaRecorder.addEventListener("stop", onStop);
        mediaRecorder.stop();
      });
    },
    isRecording: () => mediaRecorder.state === "recording",
  };
}
